class ChatWebSocket {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.eventHandlers = new Map();
    this.currentSessionId = null;
  }

  connect(sessionId) {
    if (this.ws && this.isConnected && this.currentSessionId === sessionId) {
      return Promise.resolve();
    }

    this.currentSessionId = sessionId;
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    const wsUrl = `${baseUrl.startsWith('http') ? baseUrl.replace('http', 'ws') : `ws://localhost:8000${baseUrl}`}/chat/ws/session/${sessionId}`;
    
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl);
        
        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.emit('connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
          this.isConnected = false;
          this.emit('disconnected');
          
          // Attempt to reconnect if not a normal closure
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.attemptReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.emit('error', error);
          reject(error);
        };

      } catch (error) {
        console.error('Error creating WebSocket connection:', error);
        reject(error);
      }
    });
  }

  attemptReconnect() {
    this.reconnectAttempts++;
    console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      if (this.currentSessionId) {
        this.connect(this.currentSessionId).catch(error => {
          console.error('Reconnection failed:', error);
        });
      }
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
    this.isConnected = false;
    this.currentSessionId = null;
  }

  sendMessage(message) {
    if (this.ws && this.isConnected) {
      this.ws.send(message);
    } else {
      console.error('WebSocket is not connected');
      throw new Error('WebSocket is not connected');
    }
  }

  handleMessage(data) {
    console.log('📨 Received WebSocket message:', data);
    console.log('📨 Message type:', data.type);
    
    switch (data.type) {
      case 'message_start':
        this.emit('message_start', {
          messageId: data.id
        });
        break;
        
      case 'message_end':
        this.emit('message_end', {
          messageId: data.id
        });
        break;
        
      case 'stream_create':
        this.emit('stream_create', {
          messageId: data.id,
          streamId: data.stream_id,
          createdAt: data.created_at
        });
        break;
        
      case 'stream_delta':
        console.log('🔵 STREAM_DELTA received:', {
          messageId: data.id,
          streamId: data.stream_id,
          delta: data.delta,
          fullData: data
        });
        this.emit('stream_delta', {
          messageId: data.id,
          streamId: data.stream_id,
          delta: data.delta
        });
        break;
        
      default:
        console.log('Unknown message type:', data.type);
    }
  }

  // Event handling
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  off(event, handler) {
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    console.log(`📤 Emitting event: ${event}`, data);
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event);
      console.log(`📤 Found ${handlers.length} handlers for ${event}`);
      handlers.forEach((handler, index) => {
        try {
          console.log(`📤 Calling handler ${index + 1} for ${event}`);
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    } else {
      console.log(`📤 No handlers registered for event: ${event}`);
    }
  }

  // Cleanup
  destroy() {
    this.disconnect();
    this.eventHandlers.clear();
  }
}

export default ChatWebSocket;
